import { z } from 'zod';

import { extractByPath } from '../../utils/json.util';
import { InvalidComponentError } from './base.component';
import * as Blocks from '../blocks';

export const ComponentLiteral =
  Blocks.PropertyBlock.ComponentPropertyBlock.CustomCheckboxLiteral;

export const CheckboxComponentSchema = z.object({
  name: Blocks.PropertyBlock.NamePropertyBlock.NameBlockSchema,
  component: ComponentLiteral,
  params: z.object({
    validations: z
      .object({
        required:
          Blocks.ValidationBlock.RequiredValidation.RequireBlockSchema.optional()
      })
      .optional(),
    labels: Blocks.PropertyBlock.LabelPropertyBlock.LabelBlockSchema.optional(),
    caseDataField:
      Blocks.PropertyBlock.CaseDataPropertyBlock.CaseDataFieldSchema,
    actions: z.array(
      Blocks.PropertyBlock.ActionPropertyBlock.ActionPropertyBlockSchema
    )
  })
});
export type CheckboxComponent = z.infer<typeof CheckboxComponentSchema>;

export const CheckBoxComponentWithValueSchema = CheckboxComponentSchema.merge(
  z.object({
    value: z.boolean().optional()
  })
);
export type CheckBoxComponentWithValue = z.infer<
  typeof CheckBoxComponentWithValueSchema
>;

type isValidStructure = (structure: unknown) => structure is CheckboxComponent;
export const isValidStructure: isValidStructure = (
  structure: unknown
): structure is CheckboxComponent =>
  CheckboxComponentSchema.safeParse(structure).success;

type IsValidateData = (component: CheckboxComponent, value: unknown) => boolean;
export const isValidateData: IsValidateData = (
  component: CheckboxComponent,
  value
): boolean => {
  if (!component.params.validations) {
    return true;
  }

  const { required } = component.params.validations;
  if (required !== undefined) {
    if (
      Blocks.ValidationBlock.RequiredValidation.isRequired(value, required) ===
      false
    ) {
      return false;
    }
  }

  return typeof value === 'boolean';
};

type PopulateFromCaseDataField = (
  field: CheckboxComponent,
  inputJSON: Record<string, unknown>
) => CheckBoxComponentWithValue;
export const populateFromCaseDataField: PopulateFromCaseDataField = (
  field,
  inputJSON
) => {
  const value = extractByPath({
    json: inputJSON,
    path: field.params.caseDataField
  });
  return {
    ...field,
    value: typeof value === 'boolean' ? value : undefined
  };
};

export type BuildParams = Omit<CheckboxComponent, 'component'>;
type Build = (params: BuildParams) => CheckboxComponent | InvalidComponentError;
export const build: Build = params => {
  const component = {
    ...params,
    component: ComponentLiteral.value
  };

  const parsedResult = CheckboxComponentSchema.safeParse(component);
  if (parsedResult.success) {
    return parsedResult.data;
  }

  return new InvalidComponentError(
    ComponentLiteral.value,
    parsedResult.error,
    params
  );
};
